import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { visuallyHidden } from "@mui/utils";
import type { PropsWithChildren } from "react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation } from "react-router";

import { useAuthStore } from "@/stores/auth";

export function ProtectedRoute({ children }: PropsWithChildren) {
    const { t } = useTranslation();
    const { user, loading, loadMe, hasLoaded } = useAuthStore();
    const location = useLocation();
    useEffect(() => {
        if (!hasLoaded && !loading) {
            loadMe().catch(() => undefined);
        }
    }, [hasLoaded, loading, loadMe]);

    if (!hasLoaded) {
        // Show loading spinner while checking authentication
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100vh",
                    gap: 2,
                }}
            >
                <CircularProgress size={48} />
                <Typography sx={visuallyHidden}>
                    {t("checking-authentication")}
                </Typography>
            </Box>
        );
    }

    if (!user) {
        // Redirect to login with the current location as a search param
        const redirectUrl = `/login?redirect=${encodeURIComponent(
            location.pathname + location.search,
        )}`;
        return <Navigate to={redirectUrl} replace />;
    }

    return <>{children}</>;
}
